import {
  Bold,
  Code,
  Heading2,
  Italic,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Quote,
  SquareCode,
  type LucideIcon,
} from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/utils';

import { codeBlock, link, prefixLines, wrapSelection, type MarkdownEdit } from './markdownEditing';

type View = 'write' | 'preview' | 'split';

/** Rendered markdown, styled like the rest of Studio's long-form text. */
export function MarkdownPreview({ markdown, className }: { markdown: string; className?: string }) {
  return (
    <div
      className={cn(
        'text-sm leading-relaxed text-foreground/90 [&_a]:text-foreground [&_a]:underline [&_a]:underline-offset-2 [&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em] [&_h1]:mb-3 [&_h1]:mt-6 [&_h1]:text-xl [&_h1]:font-semibold [&_h1:first-child]:mt-0 [&_h2]:mb-3 [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-semibold [&_h2:first-child]:mt-0 [&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:font-semibold [&_hr]:my-6 [&_hr]:border-border [&_li]:my-1 [&_ol]:my-3 [&_ol]:ml-5 [&_ol]:list-decimal [&_p]:my-3 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-muted [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-semibold [&_strong]:text-foreground [&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_ul]:my-3 [&_ul]:ml-5 [&_ul]:list-disc',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    </div>
  );
}

type Tool = { key: string; label: string; icon: LucideIcon; shortcut?: string; apply: (text: string, start: number, end: number) => MarkdownEdit };

const VIEW_LABELS: Record<View, string> = { write: 'Write', preview: 'Preview', split: 'Side by side' };

const TOOLS: Tool[] = [
  { key: 'heading', label: 'Heading', icon: Heading2, apply: (t, s, e) => prefixLines(t, s, e, '## ') },
  { key: 'bold', label: 'Bold', icon: Bold, shortcut: 'b', apply: (t, s, e) => wrapSelection(t, s, e, '**', '**', 'bold text') },
  { key: 'italic', label: 'Italic', icon: Italic, shortcut: 'i', apply: (t, s, e) => wrapSelection(t, s, e, '_', '_', 'italic text') },
  { key: 'code', label: 'Inline code', icon: Code, apply: (t, s, e) => wrapSelection(t, s, e, '`', '`', 'code') },
  { key: 'link', label: 'Link', icon: Link2, shortcut: 'k', apply: link },
  { key: 'bulleted', label: 'Bulleted list', icon: List, apply: (t, s, e) => prefixLines(t, s, e, '- ') },
  { key: 'numbered', label: 'Numbered list', icon: ListOrdered, apply: (t, s, e) => prefixLines(t, s, e, '', { numbered: true }) },
  { key: 'checklist', label: 'Checklist', icon: ListChecks, apply: (t, s, e) => prefixLines(t, s, e, '- [ ] ') },
  { key: 'quote', label: 'Quote', icon: Quote, apply: (t, s, e) => prefixLines(t, s, e, '> ') },
  { key: 'codeBlock', label: 'Code block', icon: SquareCode, apply: codeBlock },
];

/**
 * A markdown editor: a formatting toolbar over a textarea, with Write,
 * Preview and side-by-side views. The value stays plain markdown. Studio's
 * frontend has the same editor with translated labels.
 */
export function MarkdownEditor({
  id,
  value,
  onChange,
  maxLength,
  placeholder,
  disabled,
  minRows = 14,
  onSelectionChange,
  toolbarEnd,
  'aria-label': ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
  /** The selected range of the markdown, as the person moves it. */
  onSelectionChange?: (selection: { start: number; end: number }) => void;
  /** Extra toolbar controls after the formatting buttons. */
  toolbarEnd?: ReactNode;
  'aria-label'?: string;
}) {
  const [view, setView] = useState<View>('write');
  const textarea = useRef<HTMLTextAreaElement>(null);

  const apply = (tool: Tool) => {
    const el = textarea.current;
    if (!el || disabled) return;
    const edit = tool.apply(value, el.selectionStart, el.selectionEnd);
    if (maxLength && edit.text.length > maxLength) return;
    onChange(edit.text);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(edit.selectionStart, edit.selectionEnd);
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
    const tool = TOOLS.find((candidate) => candidate.shortcut === event.key.toLowerCase());
    if (!tool) return;
    event.preventDefault();
    apply(tool);
  };

  const views: View[] = ['write', 'preview', 'split'];
  const showWrite = view !== 'preview';
  const showPreview = view !== 'write';

  return (
    <div className={cn('overflow-hidden rounded-xl border border-input bg-background', disabled && 'opacity-60')}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/40 px-2 py-1.5">
        <div role="toolbar" aria-label="Formatting" className="flex flex-wrap items-center gap-0.5">
          {TOOLS.map((tool) => {
            const label = tool.label;
            const hint = tool.shortcut ? `${label} (⌘/Ctrl+${tool.shortcut.toUpperCase()})` : label;
            return (
              <button
                key={tool.key}
                type="button"
                title={hint}
                aria-label={hint}
                disabled={disabled || view === 'preview'}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => apply(tool)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
              >
                <tool.icon className="h-4 w-4" aria-hidden="true" />
              </button>
            );
          })}
          {toolbarEnd ? <span className="ml-1 border-l border-border pl-1.5">{toolbarEnd}</span> : null}
        </div>
        <div role="tablist" aria-label="View" className="inline-flex items-center gap-0.5 rounded-lg bg-background p-0.5">
          {views.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={view === option}
              onClick={() => setView(option)}
              className={cn(
                'h-6 rounded-md px-2.5 text-xs font-medium text-muted-foreground transition-colors',
                option === 'split' && 'hidden lg:inline-flex lg:items-center',
                view === option && 'bg-muted text-foreground shadow-sm',
              )}
            >
              {VIEW_LABELS[option]}
            </button>
          ))}
        </div>
      </div>
      <div className={cn('grid', view === 'split' && 'lg:grid-cols-2 lg:divide-x lg:divide-border')}>
        {showWrite ? (
          <textarea
            ref={textarea}
            id={id}
            aria-label={ariaLabel}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onSelect={(event) => onSelectionChange?.({ start: event.currentTarget.selectionStart, end: event.currentTarget.selectionEnd })}
            onKeyDown={onKeyDown}
            maxLength={maxLength}
            placeholder={placeholder}
            disabled={disabled}
            rows={minRows}
            spellCheck
            className="block w-full resize-y bg-transparent px-3 py-2.5 font-mono text-[13px] leading-relaxed text-foreground placeholder:text-muted-foreground focus-visible:outline-none"
          />
        ) : null}
        {showPreview ? (
          <div className="min-h-[12rem] overflow-auto px-4 py-3" aria-live="polite">
            {value.trim() ? (
              <MarkdownPreview markdown={value} />
            ) : (
              <p className="text-sm text-muted-foreground">Nothing to preview yet.</p>
            )}
          </div>
        ) : null}
      </div>
      {maxLength ? (
        <div className="flex justify-between border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">
          <span>Markdown: **bold**, _italic_, # headings, - lists, `code`</span>
          <span className={cn(value.length > maxLength * 0.95 && 'text-amber-500')}>
            {value.length.toLocaleString()} / {maxLength.toLocaleString()}
          </span>
        </div>
      ) : null}
    </div>
  );
}
